import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useDropdownSourceContext } from "@ryde/redux";
import {
  IProductionFiltersMetadata,
  IProductionStateLogData,
  RydeUiUnit,
  useDrawers,
  useFormats,
} from "@ryde/shared";

import {
  PRODUCTION_LOGS_JOIN_DELIMITER,
  PRODUCTION_METRICS_BOOKING_IDS_DISPLAY_LIMIT,
} from "./production-logs.constants";
import type { ResolvedFilterValue } from "./production-logs.models";
import {
  getProductionFiltersMetadataCacheKey,
  getProductionMetricsFiltersDisplayOrder,
  hasAnyProductionMetricsFilter,
  isIdFilterValue,
  isIdsOnlyFilterValue,
  resolveProductionMetricsFilters,
} from "./production-logs.utils";

interface IProductionLogsStatsTabsMetricsFilters {
  state?: IProductionStateLogData;
  productionFilters?: IProductionFiltersMetadata;
}

const useResolvedFilters = ({
  filtersCacheKey,
  filters,
}: {
  filtersCacheKey: string;
  filters: IProductionFiltersMetadata | undefined;
}): Record<string, ResolvedFilterValue> => {
  const { t } = useTranslation();
  const { getDropdownLabel, getDropdownLabels } = useDropdownSourceContext();
  const { formatDateTimeNormalized } = useFormats();
  const [resolved, setResolved] = useState<Record<string, ResolvedFilterValue>>(
    {},
  );

  useEffect(() => {
    if (!filtersCacheKey || !filters) {
      setResolved({});
      return;
    }

    let cancelled = false;

    const resolveFiltersAndUpdateState = async (): Promise<void> => {
      const result = await resolveProductionMetricsFilters(filters, {
        getDropdownLabel,
        getDropdownLabels,
        t,
        formatDateTimeNormalized,
      });
      if (!cancelled) {
        setResolved(result);
      }
    };

    resolveFiltersAndUpdateState();
    return () => {
      cancelled = true;
    };
  }, [
    filters,
    filtersCacheKey,
    getDropdownLabel,
    getDropdownLabels,
    t,
    formatDateTimeNormalized,
  ]);

  return resolved;
};

export const ProductionLogsStatsTabsMetricsFilters: FC<
  IProductionLogsStatsTabsMetricsFilters
> = ({ state, productionFilters }) => {
  const { t } = useTranslation();
  const { openEditFormDrawer } = useDrawers();
  const { getDropdownLabel } = useDropdownSourceContext();
  const filtersCacheKey = useMemo(
    () => getProductionFiltersMetadataCacheKey(productionFilters ?? state),
    [productionFilters, state],
  );

  const filters = useMemo((): IProductionFiltersMetadata | undefined => {
    if (!filtersCacheKey) {
      return undefined;
    }

    return JSON.parse(filtersCacheKey) as IProductionFiltersMetadata;
  }, [filtersCacheKey]);

  const [rideTypeLabel, setRideTypeLabel] = useState<string | null>(null);

  const resolved = useResolvedFilters({ filtersCacheKey, filters });

  useEffect(() => {
    if (!filters?.rideTypeId) {
      setRideTypeLabel(null);
      return;
    }
    let cancelled = false;
    const load = async (): Promise<void> => {
      const label = await getDropdownLabel("rideType", filters!.rideTypeId);
      if (!cancelled) {
        setRideTypeLabel(label);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only refetch when rideTypeId or getter changes
  }, [filters?.rideTypeId, getDropdownLabel]);

  const handleRideTypeIdClick = useCallback(() => {
    if (filters?.rideTypeId) {
      openEditFormDrawer({ entity: "rideType", id: filters.rideTypeId });
    }
  }, [filters?.rideTypeId, openEditFormDrawer]);

  const handleIdClick = useCallback(
    (entity: RydeUiUnit, id: number) => {
      openEditFormDrawer({ entity, id });
    },
    [openEditFormDrawer],
  );

  if (!filters) {
    return null;
  }

  if (!hasAnyProductionMetricsFilter(filters)) {
    return null;
  }

  const filterDisplayOrder = getProductionMetricsFiltersDisplayOrder(t);

  return (
    <div className="log__metrics-section log__metrics-section-filters">
      <div className="log__metrics-section-title">
        {t("rideProduction.metricsFilters.title")}
      </div>
      <div className="log__metrics-filters-list">
        {filterDisplayOrder.map(({ key, label }) => {
          const value = resolved[key as string];
          if (value === undefined || value === null) {
            return null;
          }
          if (isIdFilterValue(value)) {
            if (isIdsOnlyFilterValue(value)) {
              if (!value.ids.length) {
                return null;
              }

              const hiddenBookingIdsCount =
                value.ids.length > PRODUCTION_METRICS_BOOKING_IDS_DISPLAY_LIMIT
                  ? value.ids.length -
                    PRODUCTION_METRICS_BOOKING_IDS_DISPLAY_LIMIT
                  : 0;
              const idsToRender =
                hiddenBookingIdsCount > 0
                  ? value.ids.slice(
                      0,
                      PRODUCTION_METRICS_BOOKING_IDS_DISPLAY_LIMIT,
                    )
                  : value.ids;

              return (
                <div key={key} className="log__metrics-filter-row">
                  <span className="log__metrics-filter-label">{label}:</span>
                  <span className="log__metrics-filter-value-bubble">
                    {idsToRender.map((id, i) => (
                      <span key={id}>
                        {i > 0 ? PRODUCTION_LOGS_JOIN_DELIMITER : ""}
                        <button
                          type="button"
                          className="log__metrics-filter-ride-type-id-link"
                          onClick={() => handleIdClick(value.entity, id)}
                        >
                          {id}
                        </button>
                      </span>
                    ))}
                    {hiddenBookingIdsCount > 0 && (
                      <>
                        {PRODUCTION_LOGS_JOIN_DELIMITER}+{hiddenBookingIdsCount}
                      </>
                    )}
                  </span>
                </div>
              );
            }

            if (!value.items.length) {
              return null;
            }

            return (
              <div key={key} className="log__metrics-filter-row">
                <span className="log__metrics-filter-label">{label}:</span>
                <span className="log__metrics-filter-value-bubble">
                  {value.items.map((item, i) => (
                    <span key={item.id}>
                      {i > 0 ? PRODUCTION_LOGS_JOIN_DELIMITER : ""}
                      {item.label} (
                      <button
                        type="button"
                        className="log__metrics-filter-ride-type-id-link"
                        onClick={() => handleIdClick(value.entity, item.id)}
                      >
                        {item.id}
                      </button>
                      )
                    </span>
                  ))}
                </span>
              </div>
            );
          }
          if (value === "") {
            return null;
          }
          return (
            <div key={key} className="log__metrics-filter-row">
              <span className="log__metrics-filter-label">{label}:</span>
              <span className="log__metrics-filter-value-bubble">{value}</span>
            </div>
          );
        })}
        {filters.rideTypeId && (
          <div className="log__metrics-filter-row log__metrics-filter-row-ride-type">
            <span className="log__metrics-filter-label">
              {t("rideProduction.metricsFilters.rideType")}:
            </span>
            <span className="log__metrics-filter-value-bubble">
              {rideTypeLabel ?? "—"} (
              <button
                type="button"
                className="log__metrics-filter-ride-type-id-link"
                onClick={handleRideTypeIdClick}
              >
                {filters.rideTypeId}
              </button>
              )
            </span>
          </div>
        )}
      </div>
    </div>
  );
};
